import { get } from 'svelte/store';
//Services
import { format } from 'svelte-i18n';
import { cloud, postReq } from '../services/http';
//Stores
import { user_data, user_settings } from 'src/stores/account/user';
//Helpers
import { onSettingsResponse } from './device_settings_helpers';
import { getDateByTimeZone } from './date_helper';
//Types
import type {
	LiveTariff,
	PowerPricesData,
	PowerPricesInterval,
	PowerPricesSchedule,
	PowerPricesTariff,
	TariffMode,
} from '../types/PowerPrices';
import type { CloudApiResponse } from 'src/types/responses/CloudApiResponse';
import * as selectraApi from 'src/services/selectra';
import {
	selectraLiveTariffEnabled,
	selectraIsQualified,
	selectraPrices,
	refreshSelectraPrices,
} from 'src/stores/selectra';
import { clearLiveTariffFromUserSettings, syncLiveTariffFromSelectra } from './selectra_tariff_helpers';

const $format = get(format); // format

export const getTariffByID = (tariffID: string): PowerPricesTariff => {
	const userSettings = get(user_settings);
	if (tariffID === '-1') {
		//Since this function is used only to return the name of desired tariff and we need to return live tariff's name here is the shortest way to do it.
		//power prices live tariff can be disabled but history data remains. It is visualized as -1 instead of a name. So if disabled we are returning 'Live tariff' if it's enabled we are returning it's actual name
		return {
			name: userSettings?.power_prices_live_tariff?.name || 'Live tariff',
		};
	}
	if (userSettings && userSettings.power_prices) {
		const powerPrices: PowerPricesData = JSON.parse(userSettings.power_prices);
		const liveTariff = userSettings.power_prices_live_tariff;
		if (liveTariff) {
			powerPrices.tariffs[liveTariff.id] = { ...liveTariff };
		}
		return powerPrices?.tariffs?.[tariffID];
	}
	return null;
};

export const powerPricesToIntervals = (powerPrices: PowerPricesData): PowerPricesData => {
	if (powerPrices.tariffs['0'] != null) {
		delete powerPrices.tariffs['0'];
	}
	for (const tariffID in powerPrices.tariffs) {
		const tariff: PowerPricesTariff = powerPrices.tariffs[tariffID];
		tariff.id = Number(tariffID);
		tariff.edit = true;
	}
	powerPrices.schedule = powerPrices?.intervals
		? intervalsSchedule(powerPrices)
		: createNewInterval(48);
	return powerPrices;
};

export const savePowerPrices = async (
	powerPricesData: PowerPricesData,
	showToast = true,
	toastMsg: string = '',
) => {
	const tariffs = {
			'0': { name: 'empty', price: 0 },
		},
		intervals = {};
	for (let tariffID in powerPricesData.tariffs) {
		const tariff: PowerPricesTariff = powerPricesData.tariffs[tariffID];
		tariffs[tariffID] = {
			name: tariff.name,
			price: Number(tariff.price),
			modify: tariff.modify || 0,
			bgColor: tariff.bgColor || '',
		};
	}
	for (let intervalID in powerPricesData.intervals) {
		const interval: PowerPricesInterval = powerPricesData.intervals[intervalID];
		intervals[intervalID] = {
			id: interval.id,
			tariffID: interval.tariffID,
			days: interval.days,
			from: interval.from,
			to: interval.to,
			modify: interval.modify || 0,
		};
	}
	const userTimeZone = get(user_data).timezone;
	const prices: PowerPricesData = {
		tariffs,
		intervals,
		schedule: powerPricesData.schedule,
		userTimeZone,
		tsSchedule: true,
		adaptingTimeZone: true,
	};
	const res = await postReq(
		'/v2/users/power-prices',
		{ prices },
		undefined,
		undefined,
		undefined,
		true,
	);
	if (!res?.error) {
		user_settings.set({
			...get(user_settings),
			power_prices: JSON.stringify(prices),
		});
	}
	if (showToast) onSettingsResponse(res, null, {}, true, toastMsg);
	return res;
};

export const saveTariff = (tariff: PowerPricesTariff, powerPrices: PowerPricesData): PowerPricesData => {
	tariff.modify = Date.now();
	powerPrices = deepCopyData(powerPrices);
	powerPrices.tariffs[tariff.id] = tariff;
	return powerPrices;
};

export const removeTariff = (
	tariff: PowerPricesTariff,
	powerPrices: PowerPricesData,
): PowerPricesData => {
	powerPrices = deepCopyData(powerPrices);
	for (let i = 0; i < powerPrices.schedule.length; ++i) {
		const dayOfWeek = powerPrices.schedule[i];
		for (let j = 0; j < dayOfWeek.length; ++j) {
			if (powerPrices.schedule[i][j] == tariff.id) {
				powerPrices.schedule[i][j] = 0;
			}
		}
	}
	for (let intervalID in powerPrices.intervals) {
		if (powerPrices.intervals[intervalID].tariffID == tariff.id) {
			delete powerPrices.intervals[intervalID];
		}
	}
	delete powerPrices.tariffs[tariff.id];
	return powerPrices;
};

export const saveInterval = (
	interval: PowerPricesInterval,
	powerPrices: PowerPricesData,
): PowerPricesData => {
	const intervalLength = 48,
		startIndex = hourToInterval(interval.from),
		intervalSchedule = createNewInterval(intervalLength),
		scheduleWithIntervalID = intervalsSchedule(powerPrices, true);
	let endIndex = hourToInterval(interval.to) - 1;
	if (endIndex < 0) endIndex = intervalLength + endIndex;
	else if (endIndex < startIndex) endIndex += intervalLength;
	if (powerPrices.schedule.length == 0) {
		powerPrices.schedule = createNewInterval(intervalLength);
	}
	for (let i = 0; i < interval.days.length; ++i) {
		const day = interval.days[i];
		for (let j = startIndex; j <= endIndex; ++j) {
			if (j < intervalLength) {
				const existId = scheduleWithIntervalID[day][j];
				if (existId != 0 && existId != interval.id) {
					throw new Error('interval_overlaps_other');
				}
				intervalSchedule[day][j] = interval.tariffID;
			} else {
				const nextIndex = day + 1 == intervalSchedule.length ? 0 : day + 1,
					nextSlotIndex = j - intervalLength,
					existId = scheduleWithIntervalID[nextIndex][nextSlotIndex];
				if (existId != 0 && existId != interval.id) {
					throw new Error('interval_overlaps_other');
				}
				intervalSchedule[nextIndex][nextSlotIndex] = interval.tariffID;
			}
		}
	}
	powerPrices = deepCopyData(powerPrices);
	for (let i = 0; i < powerPrices.schedule.length; ++i) {
		const dayOfWeek = powerPrices.schedule[i];
		for (let j = 0; j < dayOfWeek.length; ++j) {
			if (intervalSchedule[i][j] == interval.tariffID) {
				powerPrices.schedule[i][j] = interval.tariffID;
			} else if (scheduleWithIntervalID[i][j] == interval.id) {
				powerPrices.schedule[i][j] = 0;
			}
		}
	}
	interval.modify = Date.now();
	powerPrices.intervals[interval.id] = interval;
	return powerPrices;
};

export const removeInterval = (
	interval: PowerPricesInterval,
	powerPrices: PowerPricesData,
): PowerPricesData => {
	powerPrices = deepCopyData(powerPrices);
	const schedule = intervalsSchedule(powerPrices, true);
	for (let i = 0; i < powerPrices.schedule.length; ++i) {
		const dayOfWeek = powerPrices.schedule[i];
		for (let j = 0; j < dayOfWeek.length; ++j) {
			if (schedule[i][j] == interval.id) {
				powerPrices.schedule[i][j] = 0;
			}
		}
	}
	delete powerPrices.intervals[interval.id];
	return powerPrices;
};

export const adaptingPowerPricesByTimeZoneIfOutdated = () => {
	savePowerPricesByTimeZone(true);
};

export const savePowerPricesByTimeZone = async (updateIfOutdated = false) => {
	const userSettings = get(user_settings);
	if (userSettings?.power_prices) {
		const powerPricesData: PowerPricesData = powerPricesToIntervals(
			JSON.parse(userSettings.power_prices),
		);
		if (!updateIfOutdated || powerPricesData?.adaptingTimeZone !== true) {
			const res = await savePowerPrices(powerPricesData, false);
			console.log(
				`Power prices with new timezones (${get(user_data).timezone}) ${
					res?.isok ? '' : "don't"
				} saved successfully.`,
			);
		}
	}
};

const intervalsSchedule = (
	powerPrices: PowerPricesData,
	withIntervalID = false,
): PowerPricesSchedule => {
	const intervalLength = 48,
		schedule = createNewInterval(intervalLength),
		intervals = Object.values(powerPrices.intervals);
	for (let i = 0; i < intervals.length; ++i) {
		const interval = intervals[i],
			startIndex = hourToInterval(interval.from);
		let endIndex = hourToInterval(interval.to) - 1;
		if (endIndex < 0) endIndex = intervalLength + endIndex;
		else if (endIndex < startIndex) endIndex += intervalLength;
		for (let i = 0; i < interval.days.length; ++i) {
			const day = interval.days[i];
			for (let j = startIndex; j <= endIndex; ++j) {
				if (j < intervalLength) {
					schedule[day][j] = withIntervalID ? interval.id : interval.tariffID;
				} else {
					const nextIndex = day + 1 == schedule.length ? 0 : day + 1,
						nextSlotIndex = j - intervalLength;
					schedule[nextIndex][nextSlotIndex] = withIntervalID
						? interval.id
						: interval.tariffID;
				}
			}
		}
	}
	return schedule;
};

const hourToInterval = (hour: string) => {
	const split = hour.split(':');
	return Number(split[0]) * 2 + (Number(split[1]) >= 30 ? 1 : 0);
};

const createNewInterval = (intervalLength: number) => {
	return new Array(7).fill(0).map((s) => (s = new Array(intervalLength).fill(0)));
};

const deepCopyData = (arr: any): any => {
	return JSON.parse(JSON.stringify(arr));
};

/**
 * Enables a live tariff and updates the user settings
 * @returns {Promise<RequestResponse>} - A promise with the response from the server
 */
export const enableLiveTariff = async (_liveTariff = undefined): Promise<CloudApiResponse> => {
	const currentPrice = getActivePowerPrice();
	const liveTariff = _liveTariff ||
		get(user_settings)?.power_prices_live_tariff || {
			name: $format('title_live_tariff', { default: 'Live tariff' }),
			price: currentPrice || 1,
		};
	const url = '/v2/users/power-prices/live-tariff';

	const res: CloudApiResponse = await postReq(url, liveTariff, undefined, undefined, undefined, true);
	if (!res?.error) {
		onSettingsResponse({ isok: true }, undefined, {
			success: $format('toast_success_enable_live_tariff', {
				default: 'You have switched to live tariff.',
			}),
		});
		user_settings.set({
			...get(user_settings),
			power_prices_live_tariff: res,
		});
	} else {
		onSettingsResponse({ isok: false });
	}
	return res;
};

/**
 * Edits a live tariff and updates the user settings
 * @param {LiveTariff} liveTariff - The live tariff object to be edited
 * @returns {Promise<void>} - A promise with the response from the server
 */
export const editLiveTariff = async (liveTariff: LiveTariff): Promise<void> => {
	//change live tariff name and prise w/o changing the token
	const url = '/v2/users/power-prices/live-tariff';
	try {
		const res = await cloud.put(url, liveTariff);
		onSettingsResponse({ isok: true });
		const settings = deepCopyData(get(user_settings));
		settings.power_prices_live_tariff = res?.data;
		user_settings.set({
			...settings,
		});
	} catch (error) {
		onSettingsResponse({ isok: false });
	}
};

export const removeLiveTariff = async () => {
	let res;
	const url = '/v2/users/power-prices/live-tariff';

	try {
		res = await cloud.delete(url);
		onSettingsResponse({ isok: true }, undefined, {
			success: $format('toast_success_enable_default_tariffs', {
				default: 'You have switched to default tariffs.',
			}),
		});
		const settings = deepCopyData(get(user_settings));
		delete settings.power_prices_live_tariff;
		user_settings.set({
			...settings,
		});
	} catch (error) {
		onSettingsResponse({ isok: false });
	}

	return res;
};

export const detectTariffMode = (
	selectraEnabled = get(selectraLiveTariffEnabled),
	liveTariff = get(user_settings)?.power_prices_live_tariff,
): TariffMode => {
	if (selectraEnabled) {
		return 'selectra';
	}

	if (liveTariff?.token) {
		return 'api';
	}
	if (liveTariff) {
		return 'selectra';
	}

	return 'default';
};

export const setTariffMode = async (mode: TariffMode): Promise<boolean> => {
	const currentMode = detectTariffMode();
	if (currentMode === mode) {
		return true;
	}

	try {
		if (mode === 'default') {
			if (get(selectraLiveTariffEnabled)) {
				await selectraApi.disableLiveTariff();
				selectraLiveTariffEnabled.set(false);
				clearLiveTariffFromUserSettings();
			} else if (get(user_settings)?.power_prices_live_tariff) {
				await removeLiveTariff();
			} else {
				clearLiveTariffFromUserSettings();
			}
			return true;
		}

		if (mode === 'selectra') {
			if (!get(selectraIsQualified)) {
				return false;
			}

			if (get(selectraLiveTariffEnabled)) {
				await refreshSelectraPrices();
				syncLiveTariffFromSelectra(get(selectraPrices));
				return true;
			}

			if (get(user_settings)?.power_prices_live_tariff?.token) {
				await removeLiveTariff();
			}

			await selectraApi.enableLiveTariff();
			selectraLiveTariffEnabled.set(true);
			await refreshSelectraPrices();
			syncLiveTariffFromSelectra(get(selectraPrices));

			onSettingsResponse({ isok: true }, undefined, {
				success: $format('toast_success_enable_live_tariff', {
					default: 'You have switched to live tariff.',
				}),
			});
			return true;
		}

		if (mode === 'api') {
			if (get(selectraLiveTariffEnabled)) {
				await selectraApi.disableLiveTariff();
				selectraLiveTariffEnabled.set(false);
				clearLiveTariffFromUserSettings();
			}

			const res = await enableLiveTariff();
			return !res?.error;
		}
	} catch (error) {
		console.error('Failed to set tariff mode', mode, error);
		onSettingsResponse({ isok: false });
		return false;
	}

	return false;
};

/**
 * Gets the active power price from the user settings
 * @returns {number} - The active power price
 */
function getActivePowerPrice(): number {
	//this function is used for initialization of the live tariff's price based on current active tariff
	const userSettings = get(user_settings);
	console.log('getActivePowerPrice', userSettings?.power_prices);
	if (!userSettings?.power_prices) return 1.0;
	const schedulesMatrix = JSON.parse(userSettings?.power_prices)?.schedule;
	const date = getDateByTimeZone(get(user_data).timezone);
	const price = JSON.parse(userSettings?.power_prices).tariffs[
		schedulesMatrix[date.getDay()][hourToInterval(date.getHours().toString())]
	].price;

	return price;
}
